/* eslint-disable max-lines */
/* TODO: remove the above line and reduce the line count */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import * as Sentry from '@sentry/browser';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

import { MediaControls } from './components/MediaControls';
import { SurveillancePanel } from './components/SurveillancePanel';
import { TimelinePanel } from './components/TimelinePanel';
import BodySelectionField from 'FRONTEND/components/BodySelectionField/BodySelectionField';
import { specials } from 'FRONTEND/components/ConsultationSummary/specials';
import { getLabelFromChoiceValue, getRepeatedId } from 'FRONTEND/components/ConsultationSummary/utils';
import { selectSummarizedExams } from 'FRONTEND/selectors/consultationSelectors';
import { selectSummarizedWorkflowExams } from 'FRONTEND/selectors/workflowSelectors';
import { useGetMedicineNamesByIdsQuery } from 'FRONTEND/utils/backendApi/drugReferencesApi';
import i18n from 'FRONTEND/utils/i18n';
import { useCurrentWorkflow } from 'FRONTEND/utils/nonSerializableStorage/useCurrentWorkflow';
import { translateLabel } from 'FRONTEND/utils/translateLabel';

const hasValue = (value, isDeleted) => !isEmpty(value) && !isDeleted;

const panelSx = { minWidth: '100%', maxWidth: '95vw' };

const accordionSummarySx = (theme) => ({
  color: theme.palette.getContrastText(theme.palette.tertiary.main),
  backgroundColor: theme.palette.tertiary.main,
  border: 'none',
  display: 'flex',
  paddingX: 2,
  paddingY: 1,
  alignItems: 'center',
  borderBottom: '1px solid transparent',
  borderTopLeftRadius: theme.spacing(2),
  borderTopRightRadius: theme.spacing(2),
  minHeight: theme.spacing(5),
  '& .MuiAccordionSummary-content': {
    margin: '0',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.getContrastText(theme.palette.primary.main),
  },
  '&:hover': {
    backgroundColor: theme.palette.tertiary.dark,
  },
});

// The workflow's medicine field stores drug reference ids: resolve them to the drug names.
const ChoicesFromApiChips = ({ values, field, locale }) => {
  const currentWorkflow = useCurrentWorkflow();
  const isMedicineField = field.id === currentWorkflow?.genericFieldPaths?.medicine_choices?.surveillance_medicine?.fieldId;
  const ids = values.map((value) => value?.value ?? value).filter(Boolean);
  const { currentData: names } = useGetMedicineNamesByIdsQuery(ids, { skip: !isMedicineField || ids.length === 0 });
  return values.map((value, key) => {
    const id = value?.value ?? value;
    const label = value?.label || value;
    if (!label) {
      return null;
    }
    return <Chip key={key + id} label={names?.[id] ?? translateLabel(label, locale)} variant='outlined' />;
  });
};

const getSelectFieldValue = (fieldValue, field, locale) => {
  const values = Array.isArray(fieldValue) ? fieldValue : fieldValue.split('###');

  if (field.choicesFromApi) {
    return <ChoicesFromApiChips values={values} field={field} locale={locale} />;
  } else if (field.choices) {
    return values.map((value, key) => {
      const choiceValue = field.choices.find((choice) => choice === value || choice.value === value);
      const label = choiceValue?.label || choiceValue;
      if (!label) {
        return null;
      }
      return <Chip key={key + value} label={translateLabel(label, locale)} variant='outlined' />;
    });
  } else if (field.groupChoices) {
    return values.map((valueFromArray, key) => {
      const groupChoice = field.groupChoices.find(({ options }) => options.some(({ value }) => value === valueFromArray));
      const choiceValue = groupChoice?.options.find(({ value }) => value === valueFromArray);
      const label = choiceValue?.label || valueFromArray;
      if (!label) {
        return null;
      }
      return <Chip key={key + translateLabel(label)} label={translateLabel(label, locale)} variant='outlined' />;
    });
  }
  return values.map((value, key) => {
    const label = value?.label || value;
    if (!label) {
      return null;
    }
    return <Chip key={key + translateLabel(label)} label={translateLabel(label, locale)} variant='outlined' />;
  });
};
const getBodySelectionFieldValue = (fieldValue, field) => {
  const { id, levels, photo, height, width } = field;
  return (
    <BodySelectionField
      id={id}
      photo={photo}
      height={height}
      width={width}
      levels={levels ? JSON.parse(JSON.stringify(levels)) : null}
      values={fieldValue ? JSON.parse(fieldValue) : null}
    />
  );
};

/**
 * Get the value with its unit if it exists.
 * @param fieldValue {String|Object<{value: String, unit: String}>} The value of the field as stored in the consultation.
 * @param [fieldUnit] {String} The unit of the field as defined in the JSON exam.
 * @returns {string}
 */
const getValueWithPotentialUnit = (fieldValue, fieldUnit) => {
  // Either the unit is stored alongside the value if there were several options when it was set;
  // or it only one was available, and it is stored in the JSON exam definition;
  // or there is just no unit.
  const actualUnit = fieldValue?.unit || fieldUnit;
  const actualValue = fieldValue?.value || fieldValue;
  if (actualValue == null) { // This should never happen thanks to previous checks though
    Sentry.captureException(new Error('Value is undefined'));
  }
  if (actualUnit) {
    return `${actualValue} ${getTranslatedUnit(actualUnit)}`;
  }
  return actualValue;
};

// Manage unit translation
const getTranslatedUnit = (unit) => {
  switch (unit) {
    case 'year': {
      return i18n._('year');
    }
    case 'month': {
      return i18n._('month');
    }
    case 'day': {
      return i18n._('day');
    }
    case 'week': {
      return i18n._('week');
    }
    default:
      return unit;
  }
};

const renderFormField = ({ field, consultationExam, locale }) => {
  if (field?.repeatable) {
    // We need to iterate over the instances in the values.
    // For a given section containing "antecedent_choice" and "antecedent_notes" fields
    // we look for antecedent1_choice and antecedent1_notes and render any corresponding value.
    // We iterate as long as something is rendered, or until we reach the maxRepetitions.
    const renderedInstanceList = [];
    let renderedInstance = null;
    let counter = 1;
    if (field.component === 'Section') {
      // We repeat all the section fields.
      do {
        renderedInstance = null;
        const fieldInstance = {
          ...field,
          repeatable: false,
          id: getRepeatedId(field.id, counter),
          fields: field.fields.map((fieldInSection) => {
            return {
              ...fieldInSection,
              id: getRepeatedId(fieldInSection.id, counter),
            };
          }),
        };
        renderedInstance = renderFormField({ field: fieldInstance, consultationExam, locale });
        if (renderedInstance) {
          renderedInstanceList.push(renderedInstance);
        }
        counter++;
      } while (renderedInstance && counter <= (field.maxRepetitions ?? Infinity));
      return renderedInstanceList.length > 0 ? renderedInstanceList : null;
    } else {
      // We repeat the single field.
      do {
        renderedInstance = null;
        const fieldInstance = {
          ...field,
          repeatable: false,
          id: getRepeatedId(field.id, counter),
        };
        renderedInstance = renderFormField({ field: fieldInstance, consultationExam, locale });
        if (renderedInstance) {
          renderedInstanceList.push(renderedInstance);
        }
        counter++;
      } while (renderedInstance && counter <= (field.maxRepetitions ?? Infinity));
      return renderedInstanceList;
    }
  }

  // The fallbacks should not be reached, but they are here in case the exam values are not in the expected format for some reason.
  const examFieldValue = consultationExam?.[field.id]?.value ?? consultationExam?.[field.id] ?? null;
  const isDeleted = consultationExam?.[field.id]?.isDeleted ?? false;

  if (!hasValue(examFieldValue, isDeleted) && field?.component !== 'Section') {
    return null;
  }

  const labelString = translateLabel(field.label, locale);
  let displayedValue;

  switch (field.component) {
    case 'PeoplePickerField': {
      displayedValue = Array.isArray(examFieldValue) ? examFieldValue.join(', ') : examFieldValue;
      break;
    }
    case 'Section':
      return renderSection(field.id, field.fields, field.name || field.label, consultationExam, locale);
    case 'InputField':
      displayedValue = getValueWithPotentialUnit(examFieldValue, field.unit);
      break;
    case 'CalculatedField': {
      // The unit can be defined as an array (new format) that may hold several units
      // (e.g. surveillance_saturation). The chosen unit is stored alongside the value as a key
      // (examFieldValue = { value, unit }), so we find its definition rather than assuming the
      // first one; falling back to the first entry covers single-unit fields. The "name" can
      // itself be a translation object. We unwrap the value so getValueWithPotentialUnit does
      // not re-display the raw (untranslated) unit key stored alongside it.
      if (Array.isArray(field.unit)) {
        const storedUnit = examFieldValue?.unit;
        const matchingUnit = field.unit.find((unit) => unit.name === storedUnit) ?? field.unit[0];
        displayedValue = getValueWithPotentialUnit(examFieldValue?.value ?? examFieldValue, translateLabel(matchingUnit?.name, locale));
      } else {
        displayedValue = getValueWithPotentialUnit(examFieldValue, field.unit);
      }
      break;
    }
    case 'TextAreaField':
    case 'PhotoTakerField':
    case 'FileUploadField':
      displayedValue = examFieldValue;
      break;
    case 'RadioField':{
      // Verify if the examFieldValue is a raw "key" of the choice array. If so, we want to display the corresponding label instead of the key.
      const isChoiceKey = field.choices?.some(choice => choice.value === examFieldValue);
      if (isChoiceKey) {
        const choice = field.choices?.find(choice => choice.value === examFieldValue);
        displayedValue = choice?.label;
      } else if (Array.isArray(examFieldValue)) {
        displayedValue = examFieldValue.join(' / ');
      } else {
        displayedValue = examFieldValue;
      }
      break;
    }
    case 'DateField': {
      const isDateFieldValid = dayjs(examFieldValue).isValid();
      displayedValue = isDateFieldValid ? dayjs(examFieldValue).format('LL') : examFieldValue;
      break;
    }
    case 'DateTimeField': {
      const isDateTimeFieldValid = dayjs(examFieldValue).isValid();
      displayedValue = isDateTimeFieldValid ? dayjs(examFieldValue).format('LLL') : examFieldValue;
      break;
    }
    case 'TimeField': {
      const isTimeFieldValid = dayjs(examFieldValue).isValid();
      displayedValue = isTimeFieldValid ? dayjs(examFieldValue).format('LT') : examFieldValue;
      break;
    }
    case 'SelectField':
      displayedValue = getSelectFieldValue(examFieldValue, field, locale);
      break;
    case 'BodySelectionField':
      displayedValue = getBodySelectionFieldValue(examFieldValue, field);
      break;
    case 'MedicalField': {
      // New format is Array, and unit is stored alongside the value.
      // For retro-compat, old format can be a string directly in the field definition, or an object with "name" (that can also be an object with translation strings)
      const fieldUnit = Array.isArray(field.unit) ? consultationExam?.[field.id]?.unit : translateLabel(field.unit?.name) ?? field.unit;
      displayedValue = getValueWithPotentialUnit(examFieldValue, fieldUnit);
      break;
    }
    case 'CheckBoxField':
      // examFieldValue can be a string or an array of string
      // field.choices can be an array of string [value, value] or an array of object [{value, label}, {value, label}]
      // the label can be a string or an object {value, label} or {value, label: {ca: xxx, fr: yyy, en: zzz}}
      if (typeof examFieldValue === 'string') {
        displayedValue = getLabelFromChoiceValue({ fieldValue: examFieldValue, fieldChoices: field.choices, locale });
      } else {
        displayedValue = Object.values(examFieldValue).map((value) => getLabelFromChoiceValue({ fieldValue: value, fieldChoices: field.choices, locale })).join(', ');
      }
      break;
    case 'SpecialField':
      switch (field.special) {
        case specials.DISPATCH_SMUR:
          // The back-end sends us a string if there is only one item or an array if there are multiple items
          displayedValue = (Array.isArray(examFieldValue) ? examFieldValue : [examFieldValue]).map((stationName) => (
            <Chip
              key={stationName}
              label={stationName}
              variant='outlined'
              sx={{ mr: 0.5 }}
            />
          ));
          break;
        case specials.PATIENT_AGE:
          displayedValue = getValueWithPotentialUnit(examFieldValue, field.unit);
          break;
        default:
      }
      break;
    default:
      console.warn('Component not handled', field.component, field.id);
      displayedValue = examFieldValue;
      break;
  }
  if (!hasValue(displayedValue)) {
    return null;
  }
  const isBodySelectionField = field.component === 'BodySelectionField';
  // TODO: Consolidate 'largeLabel' with 'hideLabelInRead' if possible.
  // From a quick glance, directly using hideLabelInRead at the moment would cause a lot of undesired effects in Summary and History views.
  // For now, using largeLabel temporarily for a quick fix.
  const shouldHideLabel = field?.styles?.largeLabel;
  const col = getColumnSize(field);
  return (
    <Grid
      item
      xs={col}
      key={field.id}
      sx={{ width: isBodySelectionField ? '100%' : '49%', mb: '20px', padding: '0 15px' }}
    >
      {!shouldHideLabel && labelString && (
        <Typography
          variant='subtitle1' sx={{
            display: 'flex',
            alignItems: 'center',
            mb: '5px',
            fontWeight: '700',
          }}
        >
          {labelString}
        </Typography>
      )}
      {field.component === 'SpecialField' && field.id === 'stations_to_dispatch' && (
        displayedValue
      )}
      {field.component === 'SelectField' || field.component === 'BodySelectionField' ? (
        displayedValue
      ) : (
        <Typography variant='body2' component='div' sx={{ whiteSpace: 'pre-line', fontSize: '16px', padding: '7px 0px' }}>
          {translateLabel(displayedValue, locale)}
        </Typography>
      )}
      {field.component === 'PhotoTakerField' && (
        displayedValue.map(displayedValue => <MediaControls key={displayedValue} mediaId={displayedValue} mediaType='photo'><img src={`/files/vital-file/${displayedValue}/`} alt='photo-taker' style={{ maxWidth: '100%' }} /></MediaControls>)
      )}
      {field.component === 'FileUploadField' && field.id === 'photo' && (
        displayedValue.map(displayedValue => <MediaControls key={displayedValue} mediaId={displayedValue} mediaType={field.id}><img src={`/files/vital-file/${displayedValue}/`} alt='photo-file-upload-field' style={{ maxWidth: '100%' }} /></MediaControls>)
      )}
      {field.component === 'FileUploadField' && field.id === 'video' && (
        displayedValue.map(displayedValue => (
          <MediaControls key={displayedValue} mediaId={displayedValue} mediaType={field.id}>
            <video controls key={displayedValue} src={`/files/vital-file/${displayedValue}/`} style={{ maxWidth: '100%' }}>
              <track kind='captions' />
            </video>
          </MediaControls>
        ))
      )}
    </Grid>
  );
};

const getColumnSize = (field) => {
  if (['FileUploadField', 'PhotoTakerField', 'TextAreaField', 'Section'].includes(field.component) ||
    (field.component === 'MedicalField' && field.type === 'file') ||
    (field.component === 'SpecialField' && field.double) ||
    (field.component === 'InputField' && field.previous) ||
    field.separator) {
    return 12;
  }
  if (!field.col && (!field.conditionalShowing || !field['conditionalShowing-inline'])) {
    return 6;
  }
  return field.col ?? 1;
};

const renderSection = (sectionId, fieldsInSection, sectionName, consultationExam, locale) => {
  const sectionFields = fieldsInSection.map(field => renderFormField({ field, consultationExam, locale })).filter(Boolean);

  return sectionFields.length > 0 && (
    <Grid item xs={12} key={sectionId}>
      <Box className='panel panel-info'>
        {(sectionName && sectionFields.length) ? (
          <Typography variant='h5' sx={{ fontSize: '18px' }} className='panel-heading'>
            {translateLabel(sectionName, locale)}
          </Typography>
        ) : null}
        <Grid container spacing={1} sx={{ py: 1, px: 2 }}>
          {sectionFields}
        </Grid>
      </Box>
    </Grid>
  );
};
const ConsultationSummary = () => {
  const locale = useSelector(state => state.user.locale);
  const currentWorkflow = useCurrentWorkflow();
  const summarizedExams = useSelector((state) => selectSummarizedExams(state, currentWorkflow));
  const summarizedWorkflowExams = useSelector((state) => selectSummarizedWorkflowExams(state, currentWorkflow));
  // Since the surveillance events are retrieved ordered by type instead of date like on MC, we use a selector to reorder them as intended
  return (
    <Stack pb={1} sx={{ alignItems: 'center' }}>
      {summarizedWorkflowExams.map(({ id, name, pages }) => {
        const isSurveillance = id === currentWorkflow.surveillanceExam;
        if (!pages && !isSurveillance) {
          return (
            <Stack key={id}>
              <Typography variant='body'>Workflow Not Supported</Typography>
            </Stack>
          );
        }
        if (isSurveillance) {
          return (
            <React.Fragment key={id}>
              <SurveillancePanel panelSx={panelSx} accordionSummarySx={accordionSummarySx} />
              <TimelinePanel panelSx={panelSx} accordionSummarySx={accordionSummarySx} />
            </React.Fragment>
          );
        }
        return (
          <Stack key={id} spacing={1} className='panel panel-info' sx={panelSx}>
            <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx}>
                <Typography sx={{ fontSize: '18px' }}>{translateLabel(name, locale)}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 2 }}>
                <Grid container spacing={1}>
                  {(() => {
                    // Group fields by sub-exam if they have a correspondingSubExam property, otherwise keep them ungrouped and render normally.
                    const ungroupedFields = [];
                    const subExamGroups = new Map(); // preserves insertion order

                    // Section fields carry no value of their own, hence no correspondingSubExam;
                    // inherit it from the first descendant field that has one so the section is grouped
                    // under its sub-exam (rendered in page order) instead of floating ungrouped on top.
                    const findSubExamInChildren = (fields) => {
                      for (const child of fields ?? []) {
                        const childSubExam = summarizedExams[id]?.[child.id]?.correspondingSubExam;
                        if (childSubExam) {
                          return childSubExam;
                        }
                        const nested = findSubExamInChildren(child.fields);
                        if (nested) {
                          return nested;
                        }
                      }
                      return undefined;
                    };

                    pages.flatMap(({ fields }) => fields).forEach((field) => {
                      const subExamObj = summarizedExams[id]?.[field.id]?.correspondingSubExam ?? findSubExamInChildren(field.fields);
                      const subExamId = subExamObj?.subExamId;

                      if (subExamId) {
                        if (!subExamGroups.has(subExamId)) {
                          subExamGroups.set(subExamId, { subExamObj, fields: [] }); // Add new entry for this sub-exam.
                        }
                        subExamGroups.get(subExamId).fields.push(field); // Append field into existing sub-exam group.
                      } else {
                        ungroupedFields.push(field);
                      }
                    });

                    return (
                      <>
                        {/* Ungrouped fields render normally */}
                        {ungroupedFields.map((field) =>
                          renderFormField({
                            field,
                            consultationExam: summarizedExams[id],
                            locale,
                          }),
                        )}

                        {/* Grouped fields render under their sub-exam title */}
                        {Array.from(subExamGroups).map(([subExamId, { subExamObj, fields }]) => {
                          return (
                            <Grid item xs={12} key={subExamId} sx={{ mb: 2 }}>
                              <Typography variant='h4' fontSize={24} fontWeight='bold' sx={{ mb: 1 }}>
                                {translateLabel(subExamObj?.name, locale)}
                              </Typography>
                              <Grid container spacing={1}>
                                {fields.map((field) => {
                                  return renderFormField({
                                    field,
                                    consultationExam: summarizedExams[id],
                                    locale,
                                  });
                                })}
                              </Grid>
                            </Grid>
                          );
                        })}
                      </>
                    );
                  })()}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        );
      })}
    </Stack>
  );
};


export default ConsultationSummary;
